import React, { useState, useRef } from "react";

import { Button, IconButton } from "@mui/material";
import { AgGridReact } from "ag-grid-react";
import "ag-grid-community/styles/ag-grid.css";
import "ag-grid-community/styles/ag-theme-alpine.css";
import "ag-grid-enterprise";

import VisibilityIcon from "@mui/icons-material/Visibility";
import EditIcon from "@mui/icons-material/Edit";

import SideNav from "component/layouts/sideNav";
import Modal from "./modal";
import {
  rolesCellRenderer,
  setRoles,
} from "component/cellRenderers/renderers";
import {
  setAlertTitleText,
  setAlertConfirmDeletion,
} from "component/modal/alertModal";

import {
  ADD,
  DELETE,
  USER_MANAGEMENT,
} from "resources/constant/strings/string";
import {
  PRUSSIAN_BLUE,
  SPANISH_GREEN,
  WHITE,
} from "resources/constant/colors/theme";
import { PATH_USER_MANAGEMENT } from "resources/constant/url/url";

import { APIMASTER } from "apihandler/master";


export default function UserManagement() {
  
  const gridRef = useRef();

  const [selectedRowId, setSelectedRowId] = useState(0);
  const [selectedRowIds, setSelectedRowIds] = useState([]);

  let initialValue = {};

  const setInitialValueForAdd = () => {
    initialValue = {
      id: 0,
      userName: "",
      password: "",
      roleId: null,
      email: "",
      phone: "",
      name: "",
    };
  };

  const deleteModel = () => {
    if (selectedRowIds.length === 0) {
      return;
    }
    setAlertConfirmDeletion(
      "Confirmation needed",
      `Do you confirm on proceeding to delete ${selectedRowIds.length} record(s)?`
    ).then((consent) => {
      if (!consent.isConfirmed) return;

      console.log("delete the selected ids: ", selectedRowIds);
      APIMASTER.DeleteUsers(selectedRowIds)
        .then((resp) => resp.json())
        .then((data) => {
          if (data.statusCode === 204) {
            const selectedData = gridRef.current.api.getSelectedRows();
            gridRef.current.api.applyTransaction({ remove: selectedData });
            setAlertTitleText("Success", "The record was deleted successfully");
            setOpenModal(false);
          } else {
            setAlertTitleText(
              "Oops...",
              `Something went wrong...${data.message}`
            );
            setOpenModal(false);
          }
        });
    });
  };

  const updateModel = (model) => {
    gridRef.current.api.applyTransaction({ update: [model] });
  };

  const addModel = (model) => {
    gridRef.current.api.applyTransaction({ add: [model], addIndex: 0 });
  };

  const onRowSelectionChanged = (event) => {
    const selectedRows = event.api.getSelectedRows();
    if (selectedRows.length > 0) {
      setSelectedRowId(selectedRows[0].id);
      const ids = selectedRows.map((row) => row.id);
      setSelectedRowIds(ids);
    } else {
      setSelectedRowId(0);
      setSelectedRowIds([]);
    }
  };

  const [formTitle, setFormTitle] = useState("Add user");
  const handleEditData = (oldData) => {
    console.log(oldData);
    initialValue = {
      id: oldData.id,
      userName: oldData.userName,
      password: oldData.password,
      roleId: oldData.roleId,
      email: oldData.email,
      phone: oldData.phone,
      name: oldData.name,
    };
    setFormData(initialValue);
    setFormTitle("Edit User");
    setViewOnlyModel(false);
    setOpenModal(true);
  };

  const handleViewData = (oldData) => {
    console.log(oldData);
    initialValue = {
      id: oldData.id,
      userName: oldData.userName,
      password: oldData.password,
      roleId: oldData.roleId,
      email: oldData.email,
      phone: oldData.phone,
      name: oldData.name,
    };
    setFormData(initialValue);
    setFormTitle("View User");
    setViewOnlyModel(true);
    setOpenModal(true);
  };

  const columnDefs = [
    {
      wrapText: true,
      autoHeight: true,
      headerName: "Name",
      field: "name",
      checkboxSelection: false,
      cellStyle: {
        "border-right-color": "#e2e2e2",
      },
    },
    {
      wrapText: true,
      autoHeight: true,
      headerName: "Role",
      field: "roleId",
      valueGetter: (params) => {
        return rolesCellRenderer({ value: params.data.roleId });
      },
      cellStyle: {
        "border-right-color": "#e2e2e2",
      },
    },
    {
      wrapText: true,
      autoHeight: true,
      headerName: "User Name",
      field: "userName",
      cellStyle: {
        "border-right-color": "#e2e2e2",
      },
    },
    {
      wrapText: true,
      autoHeight: true,
      headerName: "Email",
      field: "email",
      cellStyle: {
        "border-right-color": "#e2e2e2",
      },
    },
    {
      wrapText: true,
      autoHeight: true,
      headerName: "Mobile No",
      field: "phone",
      cellStyle: {
        "border-right-color": "#e2e2e2",
      },
    },
    {
      flex: 0.6,
      headerName: "Details",
      field: "id",
      sortable: false,
      filter: false,
      floatingFilter: false,
      cellRenderer: (params) => (
        <div style={{ display: "flex", justifyContent: "center" }}>
          <IconButton
            aria-label="edit"
            onClick={() => {
              handleEditData(params.data);
            }}
          >
            <EditIcon style={{ color: PRUSSIAN_BLUE, width: "20px" }} />
          </IconButton>
          <IconButton
            aria-label="visibility"
            onClick={() => {
              handleViewData(params.data);
            }}
          >
            <VisibilityIcon style={{ color: PRUSSIAN_BLUE, width: "20px" }} />
          </IconButton>
        </div>
      ),
    },
  ];

  const defaultColDef = {
    resizable: true,
    sortable: true,
    editable: false,
    flex: 1,
    filter: "agTextColumnFilter",
    filterParams: {
      caseSensitive: false,
      defaultOption: "startsWith",
    },
    floatingFilter: true,
  };

  const onGridReady = (params) => {
    APIMASTER.RoleOptions()
      .then((resp) => resp.json())
      .then((resp) => {
        setRoles(resp.response);

        APIMASTER.Users()
          .then((resp) => resp.json())
          .then((resp) => {
            params.api.applyTransaction({ add: resp.response });
          });
      });
  };

  const [openModal, setOpenModal] = useState(false);
  const [formData, setFormData] = useState(initialValue);
  const [viewOnlyModel, setViewOnlyModel] = useState(false);

  return (
    <SideNav
      currentComponent={USER_MANAGEMENT}
      subComponent={USER_MANAGEMENT}
      subComponentPath={PATH_USER_MANAGEMENT}
      detailComponent=""
    >
      {openModal && (
        <Modal
          style={{ align: "center" }}
          setOpenModal={setOpenModal}
          updateModel={updateModel}
          addModel={addModel}
          viewModel={viewOnlyModel}
          model={formData}
          formTitle={formTitle}
          event
        ></Modal>
      )}
      <div
        style={{
          display: "flex",
          flexDirection: "row-reverse",
          marginBottom: 10,
          marginRight: 10,
        }}
      >
        <Button
          style={{
            backgroundColor: SPANISH_GREEN,
            textTransform: "capitalize",
            fontSize: 14,
            color: WHITE,
            minWidth: "180px",
            minHeight: "45px",
          }}
          onClick={() => {
            setInitialValueForAdd();
            setFormData(initialValue);
            setFormTitle("Add User");
            setViewOnlyModel(false);
            setOpenModal(true);
          }}
        >
          {ADD}
        </Button>

        <Button
          onClick={() => {
            deleteModel();
          }}
          variant="outlined"
          sx={{
            borderColor: SPANISH_GREEN,
          }}
          style={{
            textTransform: "capitalize",
            fontSize: 14,
            color: SPANISH_GREEN,
            minWidth: "180px",
            minHeight: "45px",
            marginRight: 10,
          }}
        >
          {DELETE}
        </Button>
      </div>
      <div className="App">
        <div className="ag-theme-alpine" style={{ height: "570px" }}>
          <AgGridReact
            copyGroupHeadersToClipboard={true}
            enableRangeSelection={true}
            columnDefs={columnDefs}
            ref={gridRef}
            defaultColDef={defaultColDef}
            onGridReady={onGridReady}
            paginationAutoPageSize={true}
            pagination={true}
            rowSelection="multiple"
            getRowId={(params) => params.data.id}
            onSelectionChanged={onRowSelectionChanged}
            suppressBrowserResizeObserver={true}
          ></AgGridReact>
        </div>
      </div>
    </SideNav>
  );
}
